import { useCallback, type ComponentProps } from "react";

import { getImageUrl } from "@/lib/aws-amplify/host-storage";
import dayjs from "@/lib/dayjs";
import type { VoucherForCampaign } from "@/lib/gql/__generated__/graphql";

import { IconButtonList } from "@/components/features/items/edit/_shared/ui/components/system/list/list-item/list-icon-buttons";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { Image } from "@/components/ui/image/image";
import { TableCell, TableRow } from "@/components/ui/table/table";

type Props = {
	campaign: VoucherForCampaign;

	navigateToEditOrDetailsPage: (voucherID: string, inSale: boolean) => void;

	iconButtonActionsList: ComponentProps<typeof IconButtonList>;
};

export const SAMPLE_CAMPAIGN_ID = "ac6N8I2XTRH5Xc74";

export const CampaignTableRow = ({
	campaign,
	navigateToEditOrDetailsPage,

	iconButtonActionsList,
}: Props) => {
	const {
		voucherID: campaignID,
		inSale,
		cinStart,
		cinEnd,
		voucherGiftList,
		resourceAvailablePrice,
		mainKeyVisual,
		voucherPrice,
		createdAt,
	} = campaign;

	// 発行枚数
	const issuedCount = voucherGiftList.length;
	// 割引金額のテキスト
	const discountPriceText = `${resourceAvailablePrice.amount.toLocaleString()}円以上の予約で${voucherPrice.amount.toLocaleString()}円`;

	const createdDate = dayjs(createdAt).format("YYYY年MM月DD日");
	const issueDateText = inSale ? createdDate : "未発行";

	const imageURL = getImageUrl(mainKeyVisual);

	const cinStartText = dayjs(cinStart).format("YYYY年MM月DD日");
	const cinEndText = dayjs(cinEnd).format("YYYY年MM月DD日");
	const cinDateText = `${cinStartText} 〜 ${cinEndText}`;

	const handleClickTableRaw = useCallback(() => {
		const isSampleCampaign = campaignID === SAMPLE_CAMPAIGN_ID;
		if (isSampleCampaign) {
			notifyUserEventInfo({
				variant: "error",
				title: "見本は編集できません。\n新規作成ボタンからキャンペーンを作成してください！",
			});
			return;
		}

		navigateToEditOrDetailsPage(campaign.voucherID, campaign.inSale);
	}, [campaign.inSale, campaign.voucherID, campaignID, navigateToEditOrDetailsPage]);

	return (
		<TableRow key={campaignID} className="group cursor-pointer select-none rounded" onClick={handleClickTableRaw}>
			<TableCell>
				<Image src={imageURL} className="h-16 w-16 min-w-16 shrink-0 rounded object-cover" />
			</TableCell>
			<TableCell>
				<p className="line-clamp-3 w-52 underline-offset-4 group-hover:underline">{campaign.name}</p>
			</TableCell>
			<TableCell>
				<p className="w-48">{issueDateText}</p>
			</TableCell>
			<TableCell>
				<p className="w-36">{cinDateText}</p>
			</TableCell>
			<TableCell>
				<p className="w-12 text-center">{issuedCount}</p>
			</TableCell>
			<TableCell>
				<p className="w-52">{discountPriceText}</p>
			</TableCell>
			<TableCell>
				<IconButtonList {...iconButtonActionsList} />
			</TableCell>
		</TableRow>
	);
};

CampaignTableRow.displayName = "CampaignTableRow";
